import { Command } from 'commander';
import chalk from 'chalk';
import { basename, relative } from 'path';
import { ApiClient } from '../client.js';
import { confirm } from '../prompt.js';
import { detectWorkspace, isProductionFolder } from '../config-as-code/workspace.js';
import { readRepoConfig } from '../config-as-code/repo-config.js';
import { requireActiveOrg } from '../require-active-org.js';
import {
  assertBaseMatchesBinding,
  assertNoBindingBlocksBaseCreation,
  autoBindIfUnbound,
} from '../base-binding.js';
import { resolveTargetFolder } from '../config-as-code/resolve.js';
import { parseBaseFolder } from '../config-as-code/config-parser.js';
import { writeBaseFolder } from '../config-as-code/config-writer.js';
import { applyConfig, createPreview, deleteEntity, diffConfig, getBaseRecord } from '../config-as-code/api.js';
import { collectRemovals, isDiffEmpty, renderDiff } from '../config-as-code/diff-format.js';

export const pushCommand = new Command('push')
  .description('Push local config files to a preview database (creating it if new)')
  .argument('[database]', 'Preview database folder/id (defaults to the enclosing / only / default folder)')
  .option('-y, --yes', 'Skip confirmation prompts')
  .option('--dry-run', 'Show the diff and exit without applying')
  .option('--prune', 'Also delete entities that exist on the server but not in your files')
  .action(async (selector: string | undefined, _opts, command: Command) => {
    const opts = command.optsWithGlobals() as { base?: string; yes?: boolean; dryRun?: boolean; prune?: boolean };
    const sel = selector ?? opts.base;

    await requireActiveOrg();

    const ws = detectWorkspace();
    if (!ws) throw new Error('Not inside a git repository — rekor-ws lives at the repo root.');

    const repo = readRepoConfig(ws);
    const target = resolveTargetFolder(ws, sel, repo?.default_database);
    if (!target.exists) {
      throw new Error(`No base.yaml in ${target.folder}. Pull an existing preview, or scaffold a folder with origin_base_id set to create one.`);
    }

    const { meta, config } = parseBaseFolder(target.folder);

    // Defense-in-depth: refuse a read-only production mirror before any network round-trip (the
    // backend PUT /config and the post-fetch env check below also block it, but only after a call).
    if (isProductionFolder(meta)) {
      throw new Error(
        `${relative(ws.gitRoot, target.folder)} is a read-only production mirror. Push operates on previews — edit the linked preview and promote with 'rekor bases promote'.`,
      );
    }

    const client = new ApiClient();

    // Resolve the target id, creating the preview from its production origin if this folder is new.
    let id = meta.base_id;
    if (!id) {
      if (!meta.origin_base_id) {
        throw new Error('base.yaml needs a base_id (existing preview) or an origin_base_id (to create one).');
      }
      const name = meta.name ?? basename(target.folder);
      // Routing guard: don't spin up a new preview in a worktree already bound to a different db.
      assertNoBindingBlocksBaseCreation(name);
      if (!opts.yes) {
        const ok = await confirm(`Create a new preview '${name}' from production '${meta.origin_base_id}'?`);
        if (!ok) {
          console.log('Aborted.');
          return;
        }
      }
      const record = await createPreview(client, meta.origin_base_id, name, meta.description);
      id = record.id;
      writeBaseFolder(target.folder, { ...meta, base_id: id, environment: 'preview' }, config);
      console.log(chalk.green(`Created preview '${id}'.`));
      // Bind to the freshly created preview now — it exists server-side even on a --dry-run push.
      autoBindIfUnbound(id);
    } else {
      const record = await getBaseRecord(client, id);
      if (record.environment && record.environment !== 'preview') {
        throw new Error(`'${id}' is a ${record.environment} database. Push operates on previews — promote with 'rekor bases promote'.`);
      }
      // Routing guard: fail before diffing/applying against the wrong preview.
      assertBaseMatchesBinding(id);
    }

    const diff = await diffConfig(client, id, config);
    if (isDiffEmpty(diff)) {
      console.log(`No changes — preview '${id}' already matches your files.`);
      // A no-op push still confirms this worktree targets `id` — honor "first successful push binds".
      // Skip on --dry-run, which must stay side-effect-free.
      if (!opts.dryRun) autoBindIfUnbound(id);
      return;
    }
    console.log(renderDiff(diff));

    if (opts.dryRun) {
      console.log(chalk.dim('\nDry run — nothing applied.'));
      return;
    }

    if (!opts.yes) {
      const ok = await confirm(`\nApply these changes to preview '${id}'?`);
      if (!ok) {
        console.log('Aborted.');
        return;
      }
    }

    const result = await applyConfig(client, id, config);

    // Freshly generated secrets for added inbound webhooks/triggers — shown once.
    const secrets = { ...result.new_trigger_secrets, ...result.new_inbound_webhook_secrets };
    if (Object.keys(secrets).length > 0) {
      console.log(chalk.yellow('\nNew secrets (shown once — save them now):'));
      for (const [eid, s] of Object.entries(secrets)) console.log(`  ${eid}: ${s}`);
    }

    // Deletions are opt-in (record_type deletes cascade to documents) and separately confirmed.
    const removals = collectRemovals(diff);
    if (removals.length > 0) {
      const word = removals.length === 1 ? 'entity' : 'entities';
      if (!opts.prune) {
        console.log(chalk.red(`\n${removals.length} ${word} not in your files were left on the server. Re-run with --prune to delete.`));
      } else {
        const ok = opts.yes || (await confirm(`\nDelete ${removals.length} server ${word} not in your files? RecordTypes cascade to their documents.`));
        if (ok) {
          for (const r of removals) {
            await deleteEntity(client, id, r.kind, r.id);
            console.log(chalk.red(`  - deleted ${r.kind} ${r.id}`));
          }
        } else {
          console.log('Skipped deletions.');
        }
      }
    }

    console.log(chalk.green(`\nPushed to preview '${id}'.`));
    if (meta.origin_base_id) {
      console.log(chalk.dim(`Promote to production: rekor bases promote ${meta.origin_base_id} --from ${id}`));
    }

    // Bind after a real apply (skipped on --dry-run, which returns above). No-op when already bound.
    autoBindIfUnbound(id);
  });
